// Leitungsführung: Jede Leitung läuft waagerecht aus der Quelle, senkrecht auf einem „Stamm“
// und waagerecht in den Ziel-Pin. Alle Leitungen einer Quelle (= ein Netz) teilen sich einen
// Stamm. Damit sich Stämme verschiedener Netze nicht überdecken, bekommt jedes Netz eine eigene
// Spur: Überlappen sich zwei Stämme in der Höhe, wird der spätere um eine Spurbreite versetzt.

export type Point = { x: number; y: number };

export interface Net {
  id: string; // Quelle (Eingangsname oder Gatter-ID)
  src: Point;
  sinks: Point[];
}

export const LANE = 12; // Abstand zwischen zwei Spuren (SVG-Einheiten)
const CLEARANCE = 10; // Stämme mit weniger x-Abstand gelten als übereinander
const Y_PAD = 8; // Stämme, die sich in der Höhe fast berühren, ebenfalls trennen
const MAX_TRIES = 24;
const SNAP = 10;

/** Liefert für jedes Netz die x-Koordinate seines senkrechten Stamms. */
export function assignTrunks(nets: Net[]): Map<string, number> {
  const result = new Map<string, number>();
  const placed: { x: number; y0: number; y1: number }[] = [];
  // Feste Reihenfolge (von links nach rechts), damit Leitungen beim Verschieben eines
  // Gatters nicht unnötig hin- und herspringen.
  const order = [...nets].sort((a, b) => a.src.x - b.src.x || a.src.y - b.src.y || a.id.localeCompare(b.id));

  for (const net of order) {
    if (!net.sinks.length) continue;
    const ys = [net.src.y, ...net.sinks.map(p => p.y)];
    const y0 = Math.min(...ys) - Y_PAD;
    const y1 = Math.max(...ys) + Y_PAD;
    const nearest = Math.min(...net.sinks.map(p => p.x));
    const fan = net.sinks.length > 1;

    // Erlaubter Bereich: zwischen Quelle und dem nächstgelegenen Ziel
    let lo = net.src.x + LANE;
    let hi = nearest - LANE;
    if (hi < lo) hi = lo + LANE * 8; // Ziel liegt links der Quelle: Stamm rechts der Quelle
    const preferred = !fan && nearest - net.src.x >= 40
      ? Math.round((net.src.x + nearest) / 2 / SNAP) * SNAP
      : net.src.x + 20;
    const pref = Math.min(Math.max(preferred, lo), hi);

    // Rein waagerechte Leitung: braucht keine Spur
    if (y1 - y0 <= 2 * Y_PAD) {
      result.set(net.id, pref);
      continue;
    }

    let x = pref;
    for (let k = 0; k < MAX_TRIES; k++) {
      const off = Math.ceil(k / 2) * LANE * (k % 2 ? 1 : -1); // 0, +L, -L, +2L, -2L, …
      const cand = pref + off;
      if (cand < lo || cand > hi) continue;
      const blocked = placed.some(t => Math.abs(t.x - cand) < CLEARANCE && t.y0 < y1 && y0 < t.y1);
      if (!blocked) { x = cand; break; }
    }
    placed.push({ x, y0, y1 });
    result.set(net.id, x);
  }
  return result;
}

// ---------------------------------------------------------------- Umwege um Gatter

export interface Rect { x0: number; y0: number; x1: number; y1: number }

export interface Detour {
  yc: number; // Höhe, auf der die Leitung an den Hindernissen vorbeiläuft
  xt: number; // x, an dem sie vor dem Ziel-Pin auf dessen Höhe einbiegt
}

const DETOUR_GAP = 14; // Abstand der Umleitung zu Gattern
const PIN_APPROACH = 12; // senkrechtes Einbiegen so weit vor dem Ziel-Pin
const PIN_STEP = 10; // pro Eingangsnummer weiter links, damit sich Einbieger nicht überdecken

function blockers(y: number, xa: number, xb: number, rects: Rect[]): Rect[] {
  return rects.filter(r => y > r.y0 && y < r.y1 && xb > r.x0 && xa < r.x1);
}

/**
 * Prüft, ob die waagerechte Zielstrecke (Höhe des Ziel-Pins, von der Quelle bis zum Pin)
 * ein Gatter schneidet. Wenn ja, liefert sie eine Umleitung darüber oder darunter.
 * `obstacles` enthält die Gatter ohne Quell- und Zielgatter.
 */
export function planDetour(src: Point, pin: Point, pinIndex: number, obstacles: Rect[],
                           height: number): Detour | null {
  const xa = src.x + LANE;
  const xt = pin.x - PIN_APPROACH - PIN_STEP * pinIndex;
  if (xt <= xa || !blockers(pin.y, xa, pin.x, obstacles).length) return null;

  const candidates: number[] = [];
  for (const dir of [-1, 1]) {
    let y = pin.y;
    for (let k = 0; k < 6; k++) {
      const hit = blockers(y, xa, xt, obstacles);
      if (!hit.length) break;
      y = dir < 0 ? Math.min(...hit.map(r => r.y0)) - DETOUR_GAP : Math.max(...hit.map(r => r.y1)) + DETOUR_GAP;
    }
    if (y > 4 && y < height - 4 && !blockers(y, xa, xt, obstacles).length) candidates.push(y);
  }
  if (!candidates.length) return null;
  // kürzester senkrechter Gesamtweg
  const cost = (y: number) => Math.abs(y - src.y) + Math.abs(y - pin.y);
  const yc = candidates.reduce((a, b) => (cost(b) < cost(a) ? b : a));
  return { yc, xt };
}
